import { Observable } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

import { IEnclosure } from '../models/enclosure.model';
import { DataService } from './data.service';
import { UtilService } from './util.service';
import { ToastInfoEnum } from '../../toast/toast.info.enum';
import { IProject } from '../models/project.model';
import { ToastService } from '../../toast/toast.service';
import { ToastRef } from '../../toast/toast-ref';
import { IDocument } from '../models/document.model';

export abstract class FileDownload {

  protected constructor(readonly d: DataService, readonly u: UtilService, readonly t: ToastService) {
  }

  downloadFile(data: any, format: string, filename: string) {
    const blob = new Blob([data], {type: format});
    const url = window.URL.createObjectURL(blob);
    const fileLink = document.createElement('a');
    fileLink.href = url;
    fileLink.download = filename;
    fileLink.click();
  }

  protected downloadFileBlob(url: string, data, name: string, enclosureId?: number): Observable<IDocument> {
    const toastRef = this.showAndAddDownloadStartedNotification(enclosureId);
    return this.d.downloadPDF(url, data)
      .pipe(
        map(d => {
          this.downloadFile(d, 'blob', name);
          return { blob: d, name } ;
        }),
        finalize(() => {
          toastRef.close();
        }),
        catchError(e => this.handleDownloadDocumentError(e, enclosureId))
      );
  }

  protected async handleDownloadDocumentError(error, enclosureId: number) {
    const errorDetails = {
      errors: [{ details: ToastInfoEnum.FAILED_TO_DOWNLOAD_THE_DOCUMENT }]
    };

    this.u.showToastWarning(errorDetails);

    if (enclosureId !== null) {
      const [toastError] = errorDetails?.errors;
      this.t.addToastToNotifications({
        text: toastError?.details,
        type: 'warning'
      }, enclosureId)
    }

    return error;
  }

  protected generateFileName(enclosure: IEnclosure, fileType: string, prefix: string = ''): string {
    return prefix.trim() + ' - ' + enclosure.name + '.' + fileType;
  }

  protected generateProjectFileName(project: IProject, fileType: string, prefix: string = ''): string {
    return prefix.trim() + ' - ' + project.projectName + '.' + fileType;
  }

  protected generateFileNameWithoutExtension(enclosure: IEnclosure, prefix: string = ''): string {
    return prefix.trim() + ' - ' + enclosure.name;
  }

  protected generateProjectFileNameWithoutExtension(project: IProject, prefix: string = ''): string {
    return prefix.trim() + ' - ' + project.projectName;
  }

  protected showDownloadStartedNotification(): ToastRef {
    return this.u.showDownloadToastInfo({ details: ToastInfoEnum.DOWNLOAD_BEGIN });
  }

  private showAndAddDownloadStartedNotification(enclosureId?: number): ToastRef {
    const toastRef = this.showDownloadStartedNotification();
    this.t.addToastToNotifications({
      text: ToastInfoEnum.DOWNLOAD_BEGIN,
      type: 'info'
    }, enclosureId);

    return toastRef;
  }
}
